'use client';

import { formatHex, parse } from 'culori';
import { useTheme } from 'next-themes';
import QR from 'qrcode';
import {
  type HTMLAttributes,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';

import { cn } from '../../../utils/cn';

export type QRCodeProps = HTMLAttributes<HTMLDivElement> & {
  data: string;
  /** Module (ink) color. Defaults to theme token `--qr-fg`. */
  foreground?: string;
  /** Quiet-zone / plate color. Defaults to theme token `--qr-bg`. */
  background?: string;
  robustness?: 'L' | 'M' | 'Q' | 'H';
};

const FALLBACK_QR_FG = {
  light: '#12161d',
  dark: '#e7eaee',
} as const;

const FALLBACK_QR_BG = {
  light: '#fafbfc',
  dark: '#14171b',
} as const;

const toHex = (color: string, fallback: string): string => {
  const trimmed = color.trim();
  if (!trimmed) return fallback;
  try {
    const parsed = parse(trimmed);
    if (parsed) {
      return formatHex(parsed) ?? fallback;
    }
  } catch {
    // fall through
  }
  return fallback;
};

const detectTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';

  const hasDarkClass = document.documentElement.classList.contains('dark');
  const dataTheme = document.documentElement.getAttribute('data-theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  if (hasDarkClass || dataTheme === 'dark') {
    return 'dark';
  }
  if (dataTheme === 'light') {
    return 'light';
  }

  return prefersDark ? 'dark' : 'light';
};

const subscribeToMounted = () => () => {};
const getMountedSnapshot = () => true;
const getMountedServerSnapshot = () => false;

export const QRCode = ({
  data,
  foreground,
  background,
  robustness = 'M',
  className,
  ...props
}: QRCodeProps) => {
  const [svg, setSVG] = useState<string | null>(null);
  const { theme, resolvedTheme } = useTheme();
  const mounted = useSyncExternalStore(
    subscribeToMounted,
    getMountedSnapshot,
    getMountedServerSnapshot,
  );
  const [detectedTheme, setDetectedTheme] = useState<'light' | 'dark'>(() =>
    detectTheme(),
  );

  const currentTheme = detectedTheme || resolvedTheme || theme || 'light';
  const themeKey = currentTheme === 'dark' ? 'dark' : 'light';
  const themeFromHook = (resolvedTheme || theme) as
    | 'light'
    | 'dark'
    | undefined;
  const [prevThemeFromHook, setPrevThemeFromHook] = useState(themeFromHook);
  if (themeFromHook && themeFromHook !== prevThemeFromHook) {
    setPrevThemeFromHook(themeFromHook);
    setDetectedTheme(themeFromHook);
  }

  useEffect(() => {
    if (!mounted) return;

    const observer = new MutationObserver(() => {
      const newTheme = detectTheme();
      setDetectedTheme((current) =>
        current === newTheme ? current : newTheme,
      );
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });

    return () => {
      observer.disconnect();
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;

    const generateQR = async () => {
      try {
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const styles = getComputedStyle(document.documentElement);
        const tokenFg = styles.getPropertyValue('--qr-fg').trim();
        const tokenBg = styles.getPropertyValue('--qr-bg').trim();

        const patternColor = toHex(
          foreground ?? tokenFg,
          FALLBACK_QR_FG[themeKey],
        );
        const bgColor = toHex(background ?? tokenBg, FALLBACK_QR_BG[themeKey]);

        let newSvg = await QR.toString(data, {
          type: 'svg',
          color: {
            // Library names: `dark` = modules (ink), `light` = quiet zone.
            // Theme tokens may invert (light modules on dark plate).
            dark: patternColor,
            light: bgColor,
          },
          width: 200,
          errorCorrectionLevel: robustness,
          margin: 0,
        });

        const bgColorVariations = [
          bgColor,
          bgColor.toLowerCase(),
          bgColor.toUpperCase(),
        ];

        bgColorVariations.forEach((color) => {
          newSvg = newSvg.replace(
            new RegExp(`fill="${color}"`, 'gi'),
            'fill="none"',
          );
          newSvg = newSvg.replace(
            new RegExp(`fill:${color}`, 'gi'),
            'fill:none',
          );
        });

        newSvg = newSvg.replace(
          /<rect[^>]*width="200"[^>]*height="200"[^>]*>/,
          (match) => {
            if (match.includes('fill')) {
              return match.replace(/fill="[^"]*"/, 'fill="none"');
            }
            return match.replace('>', ' fill="none">');
          },
        );

        setSVG(newSvg);
      } catch (err) {
        console.error('[QRCode] Error generating QR code:', err);
      }
    };

    generateQR();
  }, [
    data,
    foreground,
    background,
    robustness,
    currentTheme,
    themeKey,
    mounted,
    theme,
    resolvedTheme,
  ]);

  if (!svg) {
    return null;
  }

  return (
    <div
      className={cn('size-full', '[&_svg]:size-full', className)}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: "Required for SVG"
      dangerouslySetInnerHTML={{ __html: svg }}
      {...props}
    />
  );
};
